import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import homeImg from "@/assets/promo/home-dashboard.webp.asset.json";
import trackerImg from "@/assets/promo/tracker-360.webp.asset.json";
import testImg from "@/assets/promo/test-tracker.webp.asset.json";
import questionImg from "@/assets/promo/question-tracker.webp.asset.json";
import toolkitImg from "@/assets/promo/toolkit.webp.asset.json";
import collegeImg from "@/assets/promo/college-predictor.webp.asset.json";
import prereqImg from "@/assets/promo/prerequisites.webp.asset.json";
import mindmapsImg from "@/assets/promo/mindmaps.webp.asset.json";

/** Static config — zero database queries. */
const SLIDES = [
  { src: homeImg.url, alt: "The Quantum MIND home screen — Your Study In Control" },
  { src: trackerImg.url, alt: "Tracker 360 Pro — track, plan, practice and improve" },
  { src: testImg.url, alt: "Test Tracker — every test gets its own schedule, score and analytics" },
  { src: questionImg.url, alt: "Question Tracker — track questions by chapter and source" },
  { src: toolkitImg.url, alt: "JEE + NEET Toolkit — calculators, constants and reference tables" },
  { src: collegeImg.url, alt: "College Predictor — IITs, NITs, IIITs and medical college cutoffs" },
  { src: prereqImg.url, alt: "Chapter Prerequisites — foundations to know before each chapter" },
  { src: mindmapsImg.url, alt: "Mindmaps — chapter mindmaps for JEE Main and NEET revision" },
];

const HINT_KEY = "qm-promo-swipe-hint-seen";

export function PromoCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);

  useEffect(() => {
    try {
      if (!sessionStorage.getItem(HINT_KEY)) setShowHint(true);
    } catch {
      /* ignore */
    }
  }, []);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    try {
      sessionStorage.setItem(HINT_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const slides = Array.from(el.children) as HTMLElement[];
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((s, i) => {
      const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActive((prev) => {
      if (prev !== best && showHint) dismissHint();
      return best;
    });
  }, [showHint, dismissHint]);

  const goTo = useCallback((i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const idx = Math.max(0, Math.min(SLIDES.length - 1, i));
    const s = el.children[idx] as HTMLElement | undefined;
    if (!s) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({
      left: s.offsetLeft - (el.clientWidth - s.offsetWidth) / 2,
      behavior: reduce ? "auto" : "smooth",
    });
  }, []);

  // Mouse drag (desktop). Touch uses native scrolling.
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse" || !trackRef.current) return;
    drag.current = { x: e.clientX, left: trackRef.current.scrollLeft, moved: false };
    trackRef.current.style.scrollSnapType = "none";
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !trackRef.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    trackRef.current.scrollLeft = drag.current.left - dx;
  };
  const endDrag = (e: React.PointerEvent) => {
    if (!drag.current || !trackRef.current) return;
    const dx = e.clientX - drag.current.x;
    drag.current = null;
    trackRef.current.style.scrollSnapType = "";
    if (Math.abs(dx) > 40) goTo(active + (dx < 0 ? 1 : -1));
    else goTo(active);
  };

  return (
    <section aria-roledescription="carousel" aria-label="The Quantum MIND tools" className="promo-carousel">
      <div className="text-center">
        <h2 className="text-[22px] font-extrabold tracking-tight sm:text-[28px]">
          Everything You Need to Prepare Smarter
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">Explore the tools inside The Quantum MIND</p>
      </div>

      <div className="relative mt-6">
        <div
          ref={trackRef}
          onScroll={onScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={(e) => drag.current && endDrag(e)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              goTo(active + 1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              goTo(active - 1);
            }
          }}
          tabIndex={0}
          className="promo-track flex snap-x snap-mandatory gap-3 overflow-x-auto px-[8%] pb-2 outline-none focus-visible:ring-2 focus-visible:ring-primary sm:px-[calc(50%-170px)]"
        >
          {SLIDES.map((s, i) => (
            <div
              key={s.src}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${SLIDES.length}`}
              data-active={i === active}
              className="promo-slide w-[84%] shrink-0 snap-center sm:w-[340px]"
            >
              <img
                src={s.src}
                alt={s.alt}
                width={941}
                height={1672}
                draggable={false}
                loading={Math.abs(i - active) <= 1 || i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                decoding="async"
                className="block aspect-[941/1672] w-full select-none rounded-[22px] object-contain shadow-[0_20px_50px_-24px_rgba(0,0,0,0.6)]"
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label="Previous image"
          onClick={() => goTo(active - 1)}
          disabled={active === 0}
          className="glass absolute left-2 top-1/2 hidden size-10 -translate-y-1/2 place-items-center rounded-full border border-hairline transition-opacity disabled:opacity-0 sm:grid lg:left-[calc(50%-230px)]"
        >
          <ChevronLeft className="size-5" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Next image"
          onClick={() => goTo(active + 1)}
          disabled={active === SLIDES.length - 1}
          className="glass absolute right-2 top-1/2 hidden size-10 -translate-y-1/2 place-items-center rounded-full border border-hairline transition-opacity disabled:opacity-0 sm:grid lg:right-[calc(50%-230px)]"
        >
          <ChevronRight className="size-5" aria-hidden />
        </button>
      </div>

      <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Choose image">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={`Go to image ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === active ? "w-5 bg-primary" : "w-2 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>

      {showHint && (
        <p className="mt-3 text-center text-xs text-muted-foreground sm:hidden">Swipe to explore →</p>
      )}
    </section>
  );
}
